import React, { useState, useEffect } from 'react';
import { 
  Dialog, 
  DialogTitle, 
  DialogContent, 
  DialogActions, 
  TextField, 
  Button,
  CircularProgress
} from '@mui/material';
import { SavedLocationCreate, SavedLocation } from '../types';
import { createSavedLocation, updateSavedLocation } from '../services/savedLocations';

interface SaveLocationDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (savedLocation: SavedLocation) => void;
  locationData: SavedLocationCreate;
  defaultName: string;
  editingLocationId: string | null;
}

const SaveLocationDialog: React.FC<SaveLocationDialogProps> = ({
  open,
  onClose,
  onSave,
  locationData,
  defaultName,
  editingLocationId
}) => {
  const [name, setName] = useState<string>(defaultName);
  const [saving, setSaving] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  // Reset name when dialog opens with new default name
  useEffect(() => {
    if (open) {
      setName(defaultName);
      setError(null);
      
      // If this is an update operation (editingLocationId exists), automatically save and close
      if (editingLocationId) {
        handleSaveWithoutDialog();
      }
    }
  }, [open, defaultName, editingLocationId]);

  const handleSaveWithoutDialog = async () => {
    if (editingLocationId) {
      try {
        setSaving(true);
        
        // Make sure we have the name correctly before updating
        const updateData = {
          ...locationData,
          name: defaultName // This ensures the name is preserved during updates
        };
        
        console.log('Updating location with data:', updateData);
        
        // Update existing location with name preserved
        const savedLocation = await updateSavedLocation(editingLocationId, updateData);
        
        onSave(savedLocation);
        onClose();
      } catch (err) {
        console.error('Error updating location:', err);
        // No need to set error since dialog is closing immediately
      } finally {
        setSaving(false);
      }
    }
  };

  const handleSave = async () => {
    if (!name.trim()) {
      setError('Please enter a name for this location');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      
      const saveData = {
        ...locationData,
        name: name.trim()
      };
      
      // Create new location (only for new locations, not updates)
      const savedLocation = await createSavedLocation(saveData);
      
      onSave(savedLocation);
      onClose();
    } catch (err) {
      console.error('Error saving location:', err);
      setError('Failed to save location. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // If this is an update operation, don't render the dialog at all
  if (editingLocationId) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Save Location
      </DialogTitle>
      <DialogContent>
        <TextField
          autoFocus
          margin="dense"
          label="Location Name"
          type="text"
          fullWidth
          value={name}
          onChange={(e) => setName(e.target.value)}
          error={!!error}
          helperText={error}
          disabled={saving}
          variant="outlined"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={saving}>
          Cancel
        </Button>
        <Button 
          onClick={handleSave} 
          color="primary" 
          disabled={saving}
          startIcon={saving ? <CircularProgress size={20} /> : null}
        >
          {saving ? 'Saving...' : 'Save'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SaveLocationDialog; 